//
// # RenameDuplicateDialog
//
// Modal dialog for the Explorer right-click "Duplicate..." and "Rename..."
// operations. Both take an existing depot design (the right-clicked node) and a
// new full path; the type/contentKind is carried by the copied content, so no
// type/PDK/stack fields are needed. Modeled on SaveAsDialog (the closest analog)
// and reuses the shared path-input toolkit (PathField / pathInput /
// CommandPaletteDialog), per the dialog guidelines.
//
// Duplicate copies the source content to the new path (source untouched, works
// even while the source is open). Rename moves the content to the new path and
// removes the old one; the server refuses it if the design is open anywhere.
//

import React, { useCallback, useEffect, useState } from "react";

import type { schema } from "GenAlphaSchema";
import { useWorkspace } from "../../context";
import { extractErrorMessage } from "../../errorUtil";
import { CommandPaletteDialog } from "./CommandPaletteDialog";
import {
  buildDesignPath,
  formatPath,
  isValidPathInput,
  isValidSegment,
  pathClashes,
} from "./pathInput";
import {
  PathField,
  PATH_FORMAT_ERROR,
  SEGMENT_FORMAT_ERROR,
  type PathFieldMessage,
} from "./PathField";

// Discriminated by operation; both carry the depot design they act on.
export type RenameDuplicateDialogMode = {
  readonly kind: "duplicate" | "rename";
  readonly sourceIdent: schema.HierPathIdent;
};

export interface RenameDuplicateDialogProps {
  readonly mode: RenameDuplicateDialogMode | null;
  readonly onClose: () => void;
}

export function RenameDuplicateDialog({
  mode,
  onClose,
}: RenameDuplicateDialogProps) {
  const workspace = useWorkspace();

  const open = mode !== null;
  const isRename = mode?.kind === "rename";

  // Form state. `pathText` is the slash-separated *parent* path; `nameText` is
  // the leaf name. The full design path is the parent segments plus the leaf.
  const [pathText, setPathText] = useState("");
  const [nameText, setNameText] = useState("");

  // Existing full design-path strings (path.join("/")), used to block clashes
  // with content already in the depot. For rename this also blocks renaming a
  // design onto its own (unchanged) path.
  const [existingPaths, setExistingPaths] = useState<ReadonlySet<string>>(
    new Set()
  );

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validation state (shown after first submit attempt or on blur)
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Seed path/name from the source ident whenever the dialog opens. Split the
  // path into parent (all but last) + leaf (last). Duplicate seeds a
  // "_copy"-suffixed leaf so the default target doesn't clash with the source;
  // rename seeds the exact leaf so the user edits from the current name.
  useEffect(() => {
    if (!mode) return;
    const path = mode.sourceIdent.path;
    const leaf = path[path.length - 1] ?? "";
    setPathText(formatPath(path.slice(0, -1)));
    setNameText(mode.kind === "duplicate" ? `${leaf}_copy` : leaf);
    setError(null);
    setTouched({});
    setSubmitting(false);
  }, [mode]);

  // Load the set of existing full design paths once so we can block clashes
  // with content already in the depot as the user types.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void workspace.listContent().then((result) => {
      if (cancelled || result.err) return;
      setExistingPaths(new Set(result.val.map((id) => id.path.join("/"))));
    });
    return () => {
      cancelled = true;
    };
  }, [open, workspace]);

  const markTouched = useCallback((field: string) => {
    setTouched((t) => ({ ...t, [field]: true }));
  }, []);

  // Validation. A parent path is required (no bare top-level leaves), as is a
  // valid leaf name.
  const pathValid = isValidPathInput(pathText);
  const nameValid = isValidSegment(nameText);

  // Full design path = parent segments + leaf name.
  const fullSegments = buildDesignPath(pathText, nameText);

  // Block clashes with existing depot content. One design per path, so any
  // existing design at this path clashes regardless of type.
  const clashes =
    pathValid && nameValid && pathClashes(existingPaths, fullSegments);

  const canSubmit = pathValid && nameValid && !clashes && !submitting;

  const title = isRename ? "Rename Design" : "Duplicate Design";
  const actionWord = isRename ? "Rename" : "Duplicate";
  const submitLabel = submitting
    ? isRename
      ? "Renaming..."
      : "Duplicating..."
    : actionWord;

  const handleSubmit = useCallback(async () => {
    if (!mode) return;
    // Mark all fields as touched to reveal validation errors.
    setTouched({ path: true, name: true });

    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);

    const fullPath = buildDesignPath(pathText, nameText);
    const result =
      mode.kind === "rename"
        ? await workspace.renameDesign(mode.sourceIdent, fullPath)
        : await workspace.duplicateDesign(mode.sourceIdent, fullPath);

    if (result.err) {
      setError(
        `Failed to ${actionWord.toLowerCase()}: ${extractErrorMessage(
          result.val
        )}`
      );
      setSubmitting(false);
      return;
    }

    onClose();
  }, [mode, canSubmit, pathText, nameText, workspace, actionWord, onClose]);

  if (!mode) return null;

  const sourceName = formatPath(mode.sourceIdent.path);

  // Parent-path field message.
  const pathMessage: PathFieldMessage =
    touched.path && !pathValid
      ? { text: PATH_FORMAT_ERROR, isError: true }
      : { text: "Parent path, e.g. library/cell.", isError: false };

  // Leaf-name field message. The clash (full-path dependent) surfaces here.
  const nameFieldOk = nameValid && !clashes;
  const nameMessage: PathFieldMessage =
    touched.name && !nameValid
      ? { text: SEGMENT_FORMAT_ERROR, isError: true }
      : clashes
      ? {
          text: `A design already exists at ${formatPath(fullSegments)}.`,
          isError: true,
        }
      : { text: "Leaf name for the new design path.", isError: false };

  return (
    <CommandPaletteDialog
      title={title}
      hint={`Enter to ${actionWord.toLowerCase()} · Escape to cancel`}
      submitLabel={submitLabel}
      canSubmit={canSubmit}
      onSubmit={() => void handleSubmit()}
      onClose={onClose}
    >
      {/* Source design */}
      <div className="add-instance-param-field">
        <label className="add-instance-param-label">Source</label>
        <div
          className="add-instance-param-input"
          style={{ color: "var(--color-ide-text-dim)" }}
        >
          {sourceName}
        </div>
      </div>

      {/* Rename warning: instance references from other designs are by path and
          will not be rewritten. */}
      {isRename && (
        <div
          className="add-instance-param-constraint"
          style={{ fontStyle: "normal" }}
        >
          Other designs that instantiate this one reference it by path. Renaming
          will not update those references — you may need to fix them manually.
        </div>
      )}

      {/* Parent path */}
      <PathField
        label="Path"
        placeholder="library/cell"
        value={pathText}
        onChange={setPathText}
        onBlur={() => {
          markTouched("path");
        }}
        invalid={touched.path && !pathValid}
        message={pathMessage}
        autoFocus
      />

      {/* Leaf name — the clash (full-path dependent) is surfaced here. */}
      <PathField
        label="Name"
        placeholder="name"
        value={nameText}
        onChange={setNameText}
        onBlur={() => {
          markTouched("name");
        }}
        invalid={touched.name && !nameFieldOk}
        message={nameMessage}
      />

      {/* Error banner */}
      {error && <span className="add-instance-param-error">{error}</span>}
    </CommandPaletteDialog>
  );
}
